/**
 * CalendarOversightPage — Executive Calendar Oversight.
 *
 * Two modes:
 *   - MONTH (O-1, Tier 1): company-at-a-glance heatmap + rollups. Aggregate,
 *     non-PHI. Clicking a day opens the DAY view for that date.
 *   - DAY (O-2, Tier 2): multi-user condensed day view — rows = people, columns
 *     = hours, timed items as blocks, undated to-dos as chips. This surface DOES
 *     show patient context (contact names, appointment attendees), so it is
 *     backed by crm:calendar:oversight:day, which audits every read and writes a
 *     phi_access_log per attendees disclosure.
 *
 * Both modes require calendar:oversight (admin + board_of_directors).
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavStep } from "../lib/NavHistory";
import {
  Activity, AlertCircle, ChevronLeft, ChevronRight, AlertTriangle, CalendarClock, ArrowLeft, ExternalLink, Search,
} from "lucide-react";
import ContactDrawer from "../components/ContactDrawer";

const LINE_LABEL = {
  appointment: "Appts", task: "Tasks", followup: "Follow-ups", reminder: "Reminders", scheduled_send: "Sends",
};
const LINE_TYPES = ["appointment", "task", "followup", "reminder", "scheduled_send"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const HOURS = Array.from({ length: 13 }, (_, i) => i + 7); // 07:00–19:00 columns

function hasPerm(user, p) { return new Set((user && user.permissions) || []).has(p); }
function localTz() { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; } }
function intensity(total) { if (!total) return 0; if (total <= 3) return 1; if (total <= 8) return 2; if (total <= 15) return 3; return 4; }

// O-4: generate a report server-side (crm:calendar:oversight:report) and save it
// via an authenticated in-memory download — bytes come back through the IPC call,
// NOT an unauthenticated S3 link. PDF arrives base64; CSV as text.
async function downloadReport(params) {
  const r = await window.zignagenixAPI.invoke("crm:calendar:oversight:report", params);
  if (!r || r.error) { window.alert((r && r.message) || "Report generation failed."); return; }
  let blob;
  if (r.format === "csv") {
    blob = new Blob([r.csv], { type: "text/csv" });
  } else {
    const bytes = Uint8Array.from(atob(r.base64), (c) => c.charCodeAt(0));
    blob = new Blob([bytes], { type: "application/pdf" });
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = r.filename; document.body.appendChild(a); a.click();
  a.remove(); URL.revokeObjectURL(url);
}
const CANNED_REPORTS = [
  { key: "today_glance", label: "Today at a Glance" },
  { key: "weekly_schedule", label: "Weekly Schedule" },
  { key: "critical_overdue", label: "Critical & Overdue" },
  { key: "treatment_load", label: "Treatment Load" },
  { key: "coverage_gaps", label: "Coverage Gaps" },
];
const HEAT = ["#f2f5f9", "#dcefe6", "#a9dcc6", "#6cc4a1", "#2E8B98"];

export default function CalendarOversightPage({ user, onNavigate }) {
  const canView = hasPerm(user, "calendar:oversight");
  const [view, setView] = useState("month");     // 'month' | 'day'
  const [selectedDate, setSelectedDate] = useState(null);
  // PR-A: clickable oversight rows open the contact slide-over drawer (same
  // phi_access_log path as the full workspace). "Full profile" deep-links out.
  const [drawerContactId, setDrawerContactId] = useState(null);
  const openDrawer = (id) => { if (id) setDrawerContactId(id); };
  // Leaving a day/search view returns to the month heatmap AND clears the contact
  // drawer id. Clearing the drawer is not cosmetic: the `drawer` var below renders
  // on `drawerContactId` alone, so a stale id would REOPEN the slide-over — on a
  // contact the user never selected — the next time any day view mounts (widest-
  // PHI page). The global-Back nav steps and BOTH in-view Back buttons share this
  // one handler so the two paths cannot diverge (the "same handler" rule).
  const backToMonth = () => { setDrawerContactId(null); setView("month"); };

  // #65 Phase C2b-2 (HIGHEST-CARE surface — widest-PHI in the app). The day and
  // search views are this page's master->detail sub-views; register ONE Back step
  // each so global Back returns to the MONTH heatmap (this page's list state)
  // before navigating away — same handler the in-view Back buttons use. Hooks are
  // UNCONDITIONAL and placed ABOVE the `!canView` early return (rules-of-hooks);
  // when access is denied `view` stays 'month' so `active` is false and no step is
  // pushed — the wiring is inert on the denied path and cannot affect a
  // permission-gated page. Labels are LITERAL static strings — they must NEVER
  // carry a staff name, patient name, contact id, or the selected DATE in any
  // form (the nav stack persists to sessionStorage; #279 leak class). Back uses
  // the shared `backToMonth` (clears the drawer too — see above).
  useNavStep({
    active: view === "day" && !!selectedDate,
    label: "Calendar day",
    onBack: backToMonth,
  });
  useNavStep({
    active: view === "search",
    label: "Calendar search",
    onBack: backToMonth,
  });
  // The contact slide-over (ContactDrawer) is a content overlay rendered on top of
  // the day/search views. C1 (#281) established that global Back CLOSES OVERLAYS
  // FIRST — so it gets its OWN step. Because it opens AFTER the day/search step, it
  // is the most-recent entry and pops first: Back #1 closes the drawer, Back #2
  // does day/search->month. LITERAL label 'Contact' — never the contact's name or
  // id. Same handler the drawer's own close (onClose) uses.
  useNavStep({
    active: !!drawerContactId,
    label: "Contact",
    onBack: () => setDrawerContactId(null),
  });

  if (!canView) {
    return (
      <div style={wrap}>
        <div style={accessDenied}>
          <AlertCircle size={16} /> You do not have permission to view calendar oversight.
        </div>
      </div>
    );
  }

  const drawer = drawerContactId != null ? (
    <ContactDrawer
      contactId={drawerContactId}
      user={user}
      onClose={() => setDrawerContactId(null)}
      onFullProfile={(id) => { setDrawerContactId(null); if (onNavigate) onNavigate("crm", { contactId: id }); }}
    />
  ) : null;

  if (view === "day" && selectedDate) {
    return <>{drawer}<DayOversight user={user} onNavigate={onNavigate} onOpenContact={openDrawer} dateISO={selectedDate} onBack={backToMonth} /></>;
  }
  if (view === "search") {
    return <>{drawer}<SearchOversight user={user} onNavigate={onNavigate} onOpenContact={openDrawer} onBack={backToMonth} /></>;
  }
  return <MonthOversight onOpenDay={(iso) => { setSelectedDate(iso); setView("day"); }} onOpenSearch={() => setView("search")} />;
}

// ── SEARCH (O-3) ─────────────────────────────────────────────────────────────
// Free-text + structured search over all calendar-borne items. All filtering
// is server-side (crm:calendar:oversight:search validates + binds every param
// and audits + phi_access_logs); this component only collects inputs + renders.
const SEARCH_TYPES = [
  { key: "appointment", label: "Appointments" },
  { key: "task", label: "Tasks" },
  { key: "followup", label: "Follow-ups" },
  { key: "reminder", label: "Reminders" },
  { key: "scheduled_send", label: "Scheduled sends" },
];
function SearchOversight({ user, onNavigate, onBack, onOpenContact }) {
  const [q, setQ] = useState("");
  const [type, setType] = useState("");           // "" = all types
  const [roleType, setRoleType] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [criticalOnly, setCriticalOnly] = useState(false);
  const [results, setResults] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const filterParams = useCallback(() => ({
    q: q.trim() || undefined,
    types: type ? [type] : undefined,
    roleType: roleType.trim() || undefined,
    from: from || undefined,
    to: to || undefined,
    criticalOnly,
    tz: localTz(),
  }), [q, type, roleType, from, to, criticalOnly]);

  const [exporting, setExporting] = useState(false);
  const exportReport = useCallback(async (reportType, format) => {
    setExporting(true);
    try { await downloadReport({ ...filterParams(), reportType, format }); }
    catch { window.alert("Export failed."); }
    finally { setExporting(false); }
  }, [filterParams]);

  const runSearch = useCallback(async (e) => {
    if (e) e.preventDefault();
    setLoading(true); setError(null);
    try {
      const params = filterParams();
      const r = await window.zignagenixAPI.invoke("crm:calendar:oversight:search", params);
      if (r && r.error) { setError(r.message || "Search failed."); setResults([]); }
      else { setResults((r && r.results) || []); setTruncated(!!(r && r.truncated)); }
    } catch (err) {
      setError("Search failed.");
      setResults([]);
    } finally { setLoading(false); }
  }, [filterParams]);

  return (
    <div style={wrap}>
      <div style={header}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={onBack} style={navBtn} aria-label="Back to month"><ArrowLeft size={16} /></button>
          <Search size={20} color="#2E8B98" />
          <h1 style={{ fontSize: 20, margin: 0 }}>Oversight Search</h1>
        </div>
      </div>

      <form onSubmit={runSearch} style={{ ...card, marginBottom: 12, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end" }}>
        <label style={fieldLbl}>Keyword
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="name, title, notes…" style={inp} aria-label="Keyword" />
        </label>
        <label style={fieldLbl}>Type
          <select value={type} onChange={(e) => setType(e.target.value)} style={inp} aria-label="Type">
            <option value="">All types</option>
            {SEARCH_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </label>
        <label style={fieldLbl}>Role
          <input value={roleType} onChange={(e) => setRoleType(e.target.value)} placeholder="e.g. physician" style={inp} aria-label="Role" />
        </label>
        <label style={fieldLbl}>From
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={inp} aria-label="From date" />
        </label>
        <label style={fieldLbl}>To
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={inp} aria-label="To date" />
        </label>
        <label style={{ ...fieldLbl, flexDirection: "row", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={criticalOnly} onChange={(e) => setCriticalOnly(e.target.checked)} aria-label="Critical only" />
          Critical only
        </label>
        <button type="submit" style={{ ...btnSecondary, background: "#2E8B98", color: "#fff", borderColor: "#2E8B98" }} disabled={loading}>
          {loading ? "Searching…" : "Search"}
        </button>
      </form>

      <div style={{ ...card, marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#5a6478", marginBottom: 8 }}>Canned reports (PDF is watermarked when it contains patient names)</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {CANNED_REPORTS.map((r) => (
            <span key={r.key} style={{ display: "inline-flex", gap: 4, alignItems: "center", border: "1px solid #e1e6ed", borderRadius: 6, padding: "4px 6px" }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>{r.label}</span>
              <button style={reportBtn} disabled={exporting} onClick={() => exportReport(r.key, "pdf")} aria-label={`${r.label} PDF`}>PDF</button>
              <button style={reportBtn} disabled={exporting} onClick={() => exportReport(r.key, "csv")} aria-label={`${r.label} CSV`}>CSV</button>
            </span>
          ))}
        </div>
      </div>

      {error && <div style={accessDenied}><AlertCircle size={16} /> {error}</div>}

      {results && !error && (
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontSize: 13, color: "#5a6478" }}>
              {results.length} result{results.length === 1 ? "" : "s"}{truncated ? " (truncated — narrow your filters)" : ""}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button style={reportBtn} disabled={exporting || !results.length} onClick={() => exportReport("search", "pdf")} aria-label="Export results PDF">Export PDF</button>
              <button style={reportBtn} disabled={exporting || !results.length} onClick={() => exportReport("search", "csv")} aria-label="Export results CSV">Export CSV</button>
            </div>
          </div>
          {results.length === 0 ? (
            <div style={{ padding: 16, color: "#5a6478", fontSize: 13 }}>No matching items.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
                <thead>
                  <tr style={{ textAlign: "left", fontSize: 12, color: "#5a6478" }}>
                    <th style={thCell}>Type</th><th style={thCell}>When</th><th style={thCell}>Owner</th>
                    <th style={thCell}>Contact</th><th style={thCell}>Title</th><th style={thCell}>Status</th><th style={thCell}></th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={`${r.type}:${r.itemId}`}
                      style={{ borderTop: "1px solid #eef1f5", fontSize: 13, cursor: r.contactId ? "pointer" : "default" }}
                      onClick={r.contactId && onOpenContact ? () => onOpenContact(r.contactId) : undefined}
                      title={r.contactId ? "Open contact" : ""}>
                      <td style={tdCell}>{r.type}</td>
                      <td style={tdCell}>{r.when ? new Date(r.when).toLocaleString() : "—"}</td>
                      <td style={tdCell}>{r.ownerName || `#${r.ownerId}`}{r.ownerRole ? ` (${r.ownerRole})` : ""}</td>
                      <td style={tdCell}>{r.contactName || "—"}</td>
                      <td style={tdCell}>{r.title || "—"}</td>
                      <td style={tdCell}>{r.critical ? <span style={{ color: "#b3261e", fontWeight: 700 }}>! {r.status || "critical"}</span> : (r.status || "—")}</td>
                      <td style={tdCell}>
                        {r.contactId && onOpenContact && (
                          <button style={navBtn} aria-label="Open contact" onClick={(e) => { e.stopPropagation(); onOpenContact(r.contactId); }}>
                            <ExternalLink size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── MONTH (O-1) ────────────────────────────────────────────────────────────────
function MonthOversight({ onOpenDay, onOpenSearch }) {
  const now = new Date();
  const [ym, setYm] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await window.zignagenixAPI.invoke("crm:calendar:oversight:month", ym.year, ym.month, localTz());
      if (r && r.error) setError(r.message || "Failed to load oversight"); else setData(r);
    } catch (e) { setError((e && e.message) || "Failed to load oversight"); }
    setLoading(false);
  }, [ym]);
  useEffect(() => { load(); }, [load]);

  function shiftMonth(delta) {
    setYm((p) => { const idx = (p.year * 12 + (p.month - 1)) + delta; return { year: Math.floor(idx / 12), month: (idx % 12) + 1 }; });
  }

  const days = (data && data.days) || {};
  const density = (data && data.density) || {};
  const rollups = (data && data.rollups) || { todayAppointments: 0, criticalDays: 0, roleCoverageGaps: [] };

  const first = new Date(Date.UTC(ym.year, ym.month - 1, 1));
  const lead = first.getUTCDay();
  const daysInMonth = new Date(Date.UTC(ym.year, ym.month, 0)).getUTCDate();
  const cells = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(`${ym.year}-${String(ym.month).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
  const monthLabel = new Date(Date.UTC(ym.year, ym.month - 1, 1)).toLocaleDateString(undefined, { month: "long", year: "numeric", timeZone: "UTC" });
  const gapCount = (rollups.roleCoverageGaps || []).reduce((a, g) => a + (g.gapDays ? g.gapDays.length : 0), 0);

  return (
    <div style={wrap}>
      <div style={header}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Activity size={22} color="#2E8B98" />
          <div>
            <h1 style={{ fontSize: 21, margin: 0 }}>Calendar Oversight</h1>
            <p style={{ color: "#5a6478", fontSize: 13, margin: 0 }}>Company-at-a-glance staff calendar load. Click a day for the multi-staff detail.</p>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={() => shiftMonth(-1)} style={navBtn} aria-label="Previous month"><ChevronLeft size={16} /></button>
          <span style={{ fontWeight: 700, minWidth: 150, textAlign: "center" }}>{monthLabel}</span>
          <button onClick={() => shiftMonth(1)} style={navBtn} aria-label="Next month"><ChevronRight size={16} /></button>
          <button onClick={() => setYm({ year: now.getFullYear(), month: now.getMonth() + 1 })} style={btnSecondary}>Today</button>
          <button onClick={onOpenSearch} style={btnSecondary} aria-label="Open oversight search">
            <Search size={14} style={{ verticalAlign: "middle", marginRight: 4 }} />Search
          </button>
        </div>
      </div>

      {error && <div style={errBox}><AlertCircle size={14} /> {error}</div>}

      <div style={cardsRow}>
        <RollupCard icon={<CalendarClock size={16} color="#2E8B98" />} label="Appointments today" value={rollups.todayAppointments} />
        <RollupCard icon={<AlertTriangle size={16} color="#b45309" />} label="Days with critical/overdue" value={rollups.criticalDays} />
        <RollupCard icon={<AlertCircle size={16} color="#b00020" />} label="Role coverage gaps (day·role)" value={gapCount}
          title={(rollups.roleCoverageGaps || []).map((g) => `${g.roleType}: ${g.gapDays ? g.gapDays.length : 0}`).join("  ·  ")} />
      </div>

      {loading ? <div style={{ padding: 24, color: "#5a6478" }}>Loading oversight…</div> : (
        <div style={card}>
          <div className="responsive-scroll">
            <div style={{ minWidth: 720 }}>
              <div style={weekHead}>{WEEKDAYS.map((w) => <div key={w} style={weekCell}>{w}</div>)}</div>
              <div style={grid}>
                {cells.map((iso, i) => {
                  if (!iso) return <div key={`b${i}`} style={{ ...dayCell, background: "transparent", border: "none" }} />;
                  const cell = days[iso] || { counts: {}, total: 0, hasCritical: false };
                  const dens = density[iso] || [];
                  const tip = dens.length ? dens.map((u) => `${u.displayName} (${u.roleType || "?"}): ${u.count}`).join("\n") : "No staff activity";
                  return (
                    <div key={iso} style={{ ...dayCell, background: HEAT[intensity(cell.total)], cursor: "pointer" }}
                      title={tip} onClick={() => onOpenDay(iso)}>
                      <div style={dayNum}>{Number(iso.slice(8, 10))}{cell.hasCritical && <span style={critDot} />}</div>
                      {cell.total > 0 && (
                        <>
                          <div style={dayTotal}>{cell.total}</div>
                          <div style={dayBreak}>{Object.entries(cell.counts).slice(0, 3).map(([t, n]) => <span key={t} style={{ whiteSpace: "nowrap" }}>{LINE_LABEL[t] || t}:{n} </span>)}</div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div style={legend}>Density{HEAT.map((c, i) => <span key={i} style={{ ...legendSwatch, background: c }} />)}more · <span style={critDot} /> critical/overdue · click a day for staff detail</div>
        </div>
      )}
    </div>
  );
}

// ── DAY (O-2) — multi-user condensed day view ────────────────────────────────────
function DayOversight({ user, onNavigate, onOpenContact, dateISO, onBack }) {
  const [filters, setFilters] = useState({ roleType: "", userIds: [], line: "", criticalOnly: false });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const canReassign = hasPerm(user, "crm:reassign");

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const payload = {
      roleType: filters.roleType || undefined,
      userIds: filters.userIds.length ? filters.userIds : undefined,
      line: filters.line || undefined,
      criticalOnly: filters.criticalOnly || undefined,
    };
    try {
      const r = await window.zignagenixAPI.invoke("crm:calendar:oversight:day", dateISO, localTz(), payload);
      if (r && r.error) setError(r.message || "Failed to load day"); else setData(r);
    } catch (e) { setError((e && e.message) || "Failed to load day"); }
    setLoading(false);
  }, [dateISO, filters]);
  useEffect(() => { load(); }, [load]);

  const rows = (data && data.rows) || [];
  const roles = [...new Set(rows.map((r) => r.roleType).filter(Boolean))].sort();
  const set = (k, v) => setFilters((p) => ({ ...p, [k]: v }));

  function hourOf(item) {
    if (!item || !item.start) return null;
    const d = new Date(item.start);
    return Number.isNaN(d.getTime()) ? null : d.getHours();
  }

  async function reassign(item) {
    if (!canReassign || !item.contactId) return;
    const newOwner = window.prompt("Reassign this contact to which user id?");
    if (!newOwner) return;
    const nid = Number(newOwner);
    if (!Number.isInteger(nid) || nid <= 0) { setError("Enter a valid numeric user id"); return; }
    try {
      const r = await window.zignagenixAPI.invoke("crm:contacts:reassign-owner", item.contactId, nid);
      if (r && r.error) setError(r.message || r.code); else load();
    } catch (e) { setError((e && e.message) || "Reassign failed"); }
  }

  return (
    <div style={wrap}>
      <div style={header}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={onBack} style={navBtn} aria-label="Back to month"><ArrowLeft size={16} /></button>
          <div>
            <h1 style={{ fontSize: 20, margin: 0 }}>Oversight · {dateISO}</h1>
            <p style={{ color: "#5a6478", fontSize: 13, margin: 0 }}>All-staff day detail. Contains patient context — every view is audited.</p>
          </div>
        </div>
      </div>

      <div style={filterBar}>
        <select value={filters.roleType} onChange={(e) => set("roleType", e.target.value)} style={filterInput}>
          <option value="">All roles</option>
          {roles.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={filters.line} onChange={(e) => set("line", e.target.value)} style={filterInput}>
          <option value="">All line types</option>
          {LINE_TYPES.map((l) => <option key={l} value={l}>{LINE_LABEL[l]}</option>)}
        </select>
        <select multiple value={filters.userIds.map(String)} style={{ ...filterInput, minHeight: 34 }}
          onChange={(e) => set("userIds", Array.from(e.target.selectedOptions).map((o) => Number(o.value)))}>
          {rows.map((r) => <option key={r.userId} value={r.userId}>{r.displayName}</option>)}
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "#3a4a5e" }}>
          <input type="checkbox" checked={filters.criticalOnly} onChange={(e) => set("criticalOnly", e.target.checked)} /> Critical only
        </label>
      </div>

      {error && <div style={errBox}><AlertCircle size={14} /> {error}</div>}

      {loading ? <div style={{ padding: 24, color: "#5a6478" }}>Loading day…</div> : rows.length === 0 ? (
        <div style={emptyState}>No staff match the current filters.</div>
      ) : (
        <div style={card}>
          <div className="responsive-scroll">
            <table style={{ ...dayGrid, minWidth: 980 }}>
              <thead>
                <tr>
                  <th style={{ ...gcell, ...gpersonHead }}>Staff</th>
                  {HOURS.map((h) => <th key={h} style={gcell}>{String(h).padStart(2, "0")}</th>)}
                  <th style={gcell}>To-dos</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const timed = row.items.filter((i) => hourOf(i) != null);
                  const todos = row.items.filter((i) => hourOf(i) == null);
                  return (
                    <tr key={row.userId}>
                      <td style={{ ...gcell, ...gpersonHead }}>
                        <div style={{ fontWeight: 600 }}>{row.displayName}</div>
                        <div style={{ fontSize: 10.5, color: "#9aa4b2" }}>{row.roleType}</div>
                      </td>
                      {HOURS.map((h) => (
                        <td key={h} style={gcell}>
                          {timed.filter((i) => hourOf(i) === h).map((i) => (
                            <Block key={i.id || `${i.kind}-${i.title}`} item={i} onNavigate={onNavigate} onOpenContact={onOpenContact} canReassign={canReassign} onReassign={reassign} />
                          ))}
                        </td>
                      ))}
                      <td style={gcell}>
                        {todos.map((i) => (
                          <Block key={i.id || `${i.kind}-${i.title}`} item={i} chip onNavigate={onNavigate} onOpenContact={onOpenContact} canReassign={canReassign} onReassign={reassign} />
                        ))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Block({ item, chip, onNavigate, onOpenContact, canReassign, onReassign }) {
  const style = chip ? todoChip : (item.critical ? blockCrit : block);
  const open = item.contactId && onOpenContact ? () => onOpenContact(item.contactId) : null;
  return (
    <div style={{ ...style, cursor: open ? "pointer" : "default" }} title={item.subtitle || item.title || ""}
      onClick={open || undefined}>
      <span style={{ fontWeight: 600 }}>{item.title || item.kind}</span>
      {item.contactName && <span style={{ color: "#5a6478" }}> · {item.contactName}</span>}
      <span style={{ marginLeft: 4, display: "inline-flex", gap: 4 }}>
        {item.contactId && onOpenContact && (
          <button title="Open contact" style={miniBtn} onClick={(e) => { e.stopPropagation(); onOpenContact(item.contactId); }}><ExternalLink size={10} /></button>
        )}
        {item.contactId && canReassign && (
          <button title="Reassign owner" style={miniBtn} onClick={(e) => { e.stopPropagation(); onReassign(item); }}>⇄</button>
        )}
      </span>
    </div>
  );
}

function RollupCard({ icon, label, value, title }) {
  return (
    <div style={rollupCard} title={title || ""}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>{icon}<span style={rollupLabel}>{label}</span></div>
      <div style={rollupValue}>{Number(value) || 0}</div>
    </div>
  );
}

// ── styles ──────────────────────────────────────────────────────────────────────
const wrap = { padding: "16px 24px", maxWidth: 1280, margin: "0 auto" };
const header = { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 };
const navBtn = { padding: "6px 8px", background: "#fff", color: "#2E8B98", border: "1px solid #cfe3e6", borderRadius: 6, cursor: "pointer", display: "inline-flex", alignItems: "center" };
const btnSecondary = { padding: "6px 12px", fontSize: 12, fontWeight: 600, background: "#fff", color: "#5a6478", border: "1px solid #DEE2E6", borderRadius: 6, cursor: "pointer" };
const cardsRow = { display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" };
const rollupCard = { flex: "1 1 180px", background: "#fff", border: "1px solid #e1e6ed", borderRadius: 8, padding: "12px 14px" };
const rollupLabel = { fontSize: 12, color: "#5a6478", fontWeight: 600 };
const rollupValue = { fontSize: 26, fontWeight: 700, color: "#1F2937", marginTop: 4 };
const card = { background: "#fff", border: "1px solid #e1e6ed", borderRadius: 8, overflow: "hidden", padding: 12 };
const weekHead = { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 4 };
const weekCell = { textAlign: "center", fontSize: 11, fontWeight: 700, color: "#6C757D", textTransform: "uppercase", letterSpacing: "0.03em" };
const grid = { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 };
const dayCell = { minHeight: 72, borderRadius: 6, border: "1px solid #e6ebf2", padding: "4px 6px", position: "relative" };
const dayNum = { fontSize: 12, fontWeight: 700, color: "#1F2937", display: "flex", alignItems: "center", gap: 4 };
const dayTotal = { fontSize: 20, fontWeight: 700, color: "#14303a", lineHeight: 1.1 };
const dayBreak = { fontSize: 9.5, color: "#3a4a5e", marginTop: 2, lineHeight: 1.3 };
const critDot = { display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: "#b00020" };
const legend = { display: "flex", alignItems: "center", gap: 4, marginTop: 12, fontSize: 11, color: "#5a6478", flexWrap: "wrap" };
const legendSwatch = { display: "inline-block", width: 14, height: 12, borderRadius: 2, border: "1px solid #d5dce6" };
const filterBar = { display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap", alignItems: "center" };
const filterInput = { padding: "6px 8px", fontSize: 12, color: "#1f2a3b", border: "1px solid #c9d6e8", borderRadius: 4, background: "#fff" };
const dayGrid = { width: "100%", borderCollapse: "collapse", fontSize: 11.5, tableLayout: "fixed" };
const gcell = { border: "1px solid #eef1f5", padding: "4px 5px", verticalAlign: "top", textAlign: "left", color: "#6C757D", fontWeight: 700, fontSize: 10.5 };
const gpersonHead = { minWidth: 130, width: 130, color: "#1F2937", background: "#f7f9fc", position: "sticky", left: 0 };
const block = { background: "#eaf4f5", border: "1px solid #cfe3e6", borderRadius: 4, padding: "2px 4px", marginBottom: 3, fontSize: 10.5, color: "#14303a" };
const blockCrit = { ...block, background: "#fdf1e7", border: "1px solid #f0c9a3" };
const todoChip = { display: "inline-block", background: "#eef1f5", borderRadius: 10, padding: "1px 8px", marginBottom: 3, marginRight: 3, fontSize: 10.5, color: "#3a4a5e" };
const miniBtn = { border: "1px solid #cfe3e6", background: "#fff", borderRadius: 3, cursor: "pointer", fontSize: 10, lineHeight: 1, padding: "1px 3px", color: "#2E8B98" };
const errBox = { display: "flex", alignItems: "center", gap: 6, margin: "0 0 12px", padding: "8px 12px", borderRadius: 4, fontSize: 12.5, background: "#fde8e8", border: "1px solid #f5a5a5", color: "#8b0000" };
const emptyState = { padding: 40, textAlign: "center", color: "#9aa4b2", fontStyle: "italic", background: "#fff", border: "1px dashed #d5dce6", borderRadius: 6 };
const accessDenied = { display: "flex", alignItems: "center", gap: 8, padding: 20, fontSize: 13, color: "#8b0000", background: "#fde8e8", border: "1px solid #f5a5a5", borderRadius: 6 };
const fieldLbl = { display: "flex", flexDirection: "column", gap: 4, fontSize: 12, fontWeight: 600, color: "#5a6478" };
const inp = { padding: "6px 8px", fontSize: 13, border: "1px solid #cfd6e0", borderRadius: 6, minWidth: 130 };
const thCell = { padding: "6px 10px", fontWeight: 600, whiteSpace: "nowrap" };
const tdCell = { padding: "6px 10px", verticalAlign: "top" };
const reportBtn = { padding: "3px 8px", fontSize: 11, fontWeight: 600, background: "#2E8B98", color: "#fff", border: "1px solid #2E8B98", borderRadius: 5, cursor: "pointer" };
